import { useState } from 'react';
import {
  ShieldCheck, ShieldAlert, AlertTriangle, AlertCircle, Info, Wand2,
  Loader2, CheckCircle2, Sparkles,
} from 'lucide-react';
import type { QualityGateVerdict, QualityGateSeverity, QualityGateIssue } from '../lib/api/types.js';
import { IdentityRulingAction } from './IdentityRulingAction.js';
import { RejudgeAction } from './RejudgeAction.js';
import { AcceptFindingAction } from './AcceptFindingAction.js';
import { editRecheck } from '../lib/qgEditRecheck.js';

interface QualityGatePanelProps {
  metadata: QualityGateVerdict;
  /** Override audit (optional). Surfaced as a footer note when set. */
  overrideBy?: string | null;
  overrideReason?: string | null;
  overriddenAt?: string | null;
  /** Selective fix batches (2026-07-30): the operator ticks individual
   *  recommendations and previews them as a batch. The parent owns the
   *  preview mutation + the commit/discard modal — this component is
   *  presentational. */
  onPreviewBatch?: (issueIndexes: number[], optionKinds?: Record<string, string>) => void;
  previewPending?: boolean;
  /** Enables the identity ruling action on identity-check findings (B2). */
  articleId?: string;
  /** Refetch the article after a ruling (its verdict may have been refreshed
   *  and the article released). */
  onIdentityRuled?: () => void;
  /** Ruling 33: Re-judge is offered on a held filing; synthesis evidence is
   *  not persisted, so a synthesis hold gets Accept only. */
  publishStatus?: string;
  isSynthesis?: boolean;
  /** The article's updated_at, for Accept's version check. */
  updatedAt?: string | null;
  /** Refetch after a re-judge finishes or a finding is accepted. */
  onFindingResolved?: () => void;
}

const VERDICT_TONE: Record<QualityGateVerdict['verdict'], {
  label: string;
  icon: typeof ShieldCheck;
  badge: string;
  ring: string;
}> = {
  PASS:               { label: 'Pass',                icon: ShieldCheck, badge: 'bg-success-100 text-success-700', ring: 'ring-success-200' },
  'PASS WITH NOTES':  { label: 'Pass with notes',     icon: ShieldCheck, badge: 'bg-primary-100 text-primary-700', ring: 'ring-primary-200' },
  'NEEDS REVISION':   { label: 'Needs revision',      icon: ShieldAlert, badge: 'bg-warning-100 text-warning-700', ring: 'ring-warning-300' },
  GATE_ERROR:         { label: 'Gate error',          icon: AlertCircle, badge: 'bg-grey-100 text-grey-700',       ring: 'ring-grey-200' },
};

const SEVERITY_TONE: Record<QualityGateSeverity, { label: string; chip: string; icon: typeof AlertTriangle }> = {
  CRITICAL: { label: 'Critical', chip: 'bg-danger-100 text-danger-800',  icon: AlertCircle },
  MAJOR:    { label: 'Major',    chip: 'bg-warning-100 text-warning-800', icon: AlertTriangle },
  MINOR:    { label: 'Minor',    chip: 'bg-primary-50 text-primary-700',  icon: Info },
};

function formatDate(iso?: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** An issue the operator can still put in a batch.
 *
 *  The rule lives server-side now (services/finding_options) and arrives as
 *  `disposition` + `options`. This used to re-implement it with the comment
 *  "Mirrors the backend's issue_is_selectable" — two copies of one rule, and
 *  a drift waiting to happen. The fallback covers a payload from a backend
 *  that has not been redeployed yet. */
function isSelectable(issue: QualityGateIssue): boolean {
  if (issue.disposition) {
    return (
      issue.disposition === 'needs_decision' &&
      issue.resolution !== 'triage_elsewhere' &&
      (issue.options?.length ?? 0) > 0
    );
  }
  const fix = (issue.fix || '').toLowerCase();
  if (!fix) return false;
  if ((issue.status || 'confirmed') === 'cleared') return false;
  if (issue.applied || issue.auto_applied) return false;
  return true;
}

/** What the operator must actually deal with. Measured 2026-09-24: of 14.4
 *  findings shown per held article, 3.2 need a decision, 8.1 assert nothing
 *  and 1.5 have already been applied to the text. Leading with the whole list
 *  is why the panel reads as "a lot of issues". */
/** Open CRITICAL/MAJOR: what keeps a verdict at NEEDS REVISION (settle_verdict). */
function holdsPublication(issue: QualityGateIssue): boolean {
  const open = (issue.status || 'confirmed') === 'confirmed';
  const sev = (issue.severity || '').toUpperCase();
  return open && (sev === 'CRITICAL' || sev === 'MAJOR');
}

function needsDecision(issue: QualityGateIssue): boolean {
  if (issue.disposition) return issue.disposition === 'needs_decision';
  return (issue.status || 'confirmed') !== 'cleared' && !issue.applied && !issue.auto_applied;
}

export function QualityGatePanel({
  metadata,
  overrideBy,
  overrideReason,
  overriddenAt,
  onPreviewBatch,
  previewPending,
  articleId,
  onIdentityRuled,
  publishStatus,
  isSynthesis,
  updatedAt,
  onFindingResolved,
}: QualityGatePanelProps) {
  const tone = VERDICT_TONE[metadata.verdict] || VERDICT_TONE.GATE_ERROR;
  const VerdictIcon = tone.icon;
  const issues = metadata.issues || [];
  const conf = metadata.confidence_summary;
  const { stale, recheckedAfterEdit, recheckedSkills, notRejudged, batchAllowed } =
    editRecheck(metadata, needsDecision);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [chosen, setChosen] = useState<Record<number, number>>({});
  const [showAll, setShowAll] = useState(false);

  const decisionIdx = issues.map((iss, i) => (needsDecision(iss) ? i : -1)).filter((i) => i >= 0);
  const restCount = issues.length - decisionIdx.length;
  const visibleIdx = showAll ? issues.map((_, i) => i) : decisionIdx;
  const selectableCount = issues.filter(isSelectable).length;
  const canBatch = !!onPreviewBatch && selectableCount > 0 && batchAllowed;

  const toggle = (idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(decisionIdx.filter((i) => isSelectable(issues[i]))));
  };

  return (
    <div className={`bg-surface rounded-xl border-2 ${tone.ring} shadow-md overflow-hidden`}>
      <div className="px-6 py-4 border-b border-grey-200 bg-grey-50">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center space-x-3">
            <VerdictIcon className="h-5 w-5 text-grey-700" />
            <h3 className="text-lg font-bold text-grey-900">Quality Gate</h3>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${tone.badge}`}>
              {tone.label}
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-grey-500">
            {metadata.risk_tier && <span>Risk: <span className="font-semibold text-grey-700">{metadata.risk_tier.toLowerCase()}</span></span>}
            {metadata.model_used && <span>Model: <span className="font-semibold text-grey-700">{metadata.model_used}</span></span>}
            {formatDate(metadata.ran_at) && <span>Ran: <span className="font-semibold text-grey-700">{formatDate(metadata.ran_at)}</span></span>}
          </div>
        </div>
      </div>

      <div className="px-6 py-4 space-y-4">
        {recheckedAfterEdit && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 flex items-start gap-2">
            <Info className="h-4 w-4 text-primary-700 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-primary-800">
              <span className="font-semibold">Re-checked after the edit</span>
              {formatDate(metadata.edit_refreshed_at) ? ` (${formatDate(metadata.edit_refreshed_at)})` : ''}:{' '}
              {recheckedSkills.length > 0 ? recheckedSkills.join(', ') : 'code checks'} re-ran on the edited text.
              {notRejudged > 0
                ? ` ${notRejudged} finding${notRejudged === 1 ? '' : 's'} below need${notRejudged === 1 ? 's' : ''} judgement and ${notRejudged === 1 ? 'was' : 'were'} not re-judged — apply its option, or override if the edit resolved it.`
                : ' Nothing below is waiting on a re-judgement.'}
            </p>
          </div>
        )}

        {stale && !recheckedAfterEdit && (
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-warning-700 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-warning-800">
              <span className="font-semibold">Verdict is stale.</span>{' '}
              The article has been edited since this verdict was recorded.
              Regenerate to re-evaluate, or override if the recorded
              judgement still applies.
            </p>
          </div>
        )}

        {metadata.devil_advocate_notes && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-grey-500 mb-1">Devil's advocate</p>
            <p className="text-sm text-grey-700 leading-relaxed italic">"{metadata.devil_advocate_notes}"</p>
          </div>
        )}

        {issues.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
              <div className="flex items-baseline gap-2 flex-wrap">
                <p className="text-xs font-semibold uppercase tracking-wide text-grey-500">
                  {showAll ? `All findings (${issues.length})` : `Needs a decision (${decisionIdx.length})`}
                </p>
                {restCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAll((v) => !v)}
                    className="text-xs text-grey-400 hover:text-grey-600 underline underline-offset-2"
                    title="Verified checks and changes already applied to the text. Kept for the audit trail."
                  >
                    {showAll ? 'show only what needs a decision' : `+${restCount} verified / already applied`}
                  </button>
                )}
              </div>
              {canBatch && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={selectAll}
                    className="text-xs font-medium text-grey-500 hover:text-grey-700 underline underline-offset-2"
                  >
                    Select all applicable ({selectableCount})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const idxs = Array.from(selected).sort((a, b) => a - b);
                      const kinds: Record<string, string> = {};
                      idxs.forEach((i) => {
                        const opt = issues[i]?.options?.[chosen[i] ?? 0];
                        if (opt) kinds[String(i)] = opt.kind;
                      });
                      onPreviewBatch!(idxs, kinds);
                    }}
                    disabled={previewPending || selected.size === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Run the copy-edit pass on ONLY the ticked recommendations, then review the result before committing or discarding it."
                  >
                    {previewPending ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        <span>Preparing preview…</span>
                      </>
                    ) : (
                      <>
                        <Wand2 className="h-3.5 w-3.5" />
                        <span>Apply selected ({selected.size})</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
            {/* Any open CRITICAL/MAJOR — applied or not. Gating on "needs a
                decision" hid the button exactly when every fix had been
                applied (NST Market Floor, 2026-09-29). The backend now also
                re-judges by rule once every holder is applied; clicking
                while that job runs returns the same job. */}
            {articleId && publishStatus === 'review_blocked' && !isSynthesis && issues.some(holdsPublication) && (
              <div className="mb-3">
                <RejudgeAction articleId={articleId} onDone={onFindingResolved} />
              </div>
            )}
            <ul className="space-y-3">
              {visibleIdx.map((idx) => {
                const issue = issues[idx];
                const sev = SEVERITY_TONE[issue.severity] || SEVERITY_TONE.MINOR;
                const SevIcon = sev.icon;
                const selectable = canBatch && isSelectable(issue);
                const cleared = (issue.status || 'confirmed') === 'cleared';
                return (
                  <li key={idx} className={`border-l-2 pl-3 ${selected.has(idx) ? 'border-primary-400' : 'border-grey-200'}`}>
                    <div className="flex items-start gap-2">
                      {canBatch && (
                        <input
                          type="checkbox"
                          className="mt-1 h-3.5 w-3.5 rounded border-grey-300 text-primary-600 focus:ring-primary-500 disabled:opacity-30"
                          checked={selected.has(idx)}
                          disabled={!selectable}
                          onChange={() => toggle(idx)}
                          aria-label={`Select issue ${idx + 1} for the fix batch`}
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${sev.chip}`}>
                            <SevIcon className="h-3 w-3" />
                            {sev.label}
                          </span>
                          <span className="text-xs text-grey-500">· {issue.source_skill}</span>
                          {cleared && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-grey-100 text-grey-600">
                              cleared
                            </span>
                          )}
                          {issue.auto_applied && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary-100 text-primary-700"
                              title="Applied automatically at generation (minor ai-language recommendations are auto-applied network policy, 2026-07-30)"
                            >
                              <Sparkles className="h-3 w-3" />
                              auto-applied
                            </span>
                          )}
                          {issue.accepted && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-success-100 text-success-700"
                              title={`${issue.accepted.reason} — ${formatDate(issue.accepted.at) ?? issue.accepted.at}`}
                            >
                              <CheckCircle2 className="h-3 w-3" />
                              accepted by {issue.accepted.by}
                            </span>
                          )}
                          {issue.applied && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-success-100 text-success-700"
                              title={`Applied by ${issue.applied.by} · ${formatDate(issue.applied.at) ?? issue.applied.at}`}
                            >
                              <CheckCircle2 className="h-3 w-3" />
                              applied
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-grey-800 leading-snug">"{issue.claim}"</p>
                        {issue.fix && (
                          <p className="text-xs text-grey-600 mt-1">
                            <span className="font-semibold">Why:</span> {issue.fix}
                          </p>
                        )}
                        {/* The action. Operator, 2026-09-24: "where a
                            suggestion is in the narrative of an issue, it is
                            not clear if it will be taken". Now it is a choice
                            on the page, and the chosen one is visible before
                            anything is applied. */}
                        {needsDecision(issue) && (issue.options?.length ?? 0) > 0 && (
                          <div className="mt-2 space-y-1">
                            {issue.options!.map((opt, oi) => {
                              const picked = (chosen[idx] ?? 0) === oi;
                              return (
                                <label
                                  key={oi}
                                  className={`flex items-start gap-2 text-xs rounded-md px-2 py-1 cursor-pointer ${
                                    picked ? 'bg-primary-50 text-primary-800' : 'text-grey-600 hover:bg-grey-50'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name={`opt-${idx}`}
                                    checked={picked}
                                    onChange={() => setChosen((p) => ({ ...p, [idx]: oi }))}
                                    className="mt-0.5"
                                  />
                                  <span>
                                    <span className="uppercase tracking-wide text-[10px] font-semibold mr-1.5">
                                      {opt.kind}
                                    </span>
                                    {opt.label}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                        {needsDecision(issue) && (issue.options?.length ?? 0) === 0 && (
                          <p className="text-xs text-grey-500 mt-2 italic">
                            No suggested action — this one needs a manual edit.
                          </p>
                        )}
                        {issue.resolution === 'triage_elsewhere' && (
                          <p className="text-xs text-amber-700 mt-2">
                            Resolved by correcting the entity binding, not by editing the article.
                          </p>
                        )}
                        {articleId && issue.source_skill === 'identity-check' && needsDecision(issue) && (
                          <IdentityRulingAction articleId={articleId} onRuled={onIdentityRuled} />
                        )}
                        {articleId && needsDecision(issue) && issue.blocks_publication === false && (
                          <AcceptFindingAction
                            articleId={articleId}
                            issueIndex={idx}
                            updatedAt={updatedAt}
                            onAccepted={onFindingResolved}
                          />
                        )}
                        {issue.awaiting_evidence && (
                          <p className="text-xs text-grey-500 mt-1 italic">
                            Asks for a lookup we should be doing — pending the evidence ledger.
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {conf && (
          <div className="text-xs text-grey-500 pt-2 border-t border-grey-100">
            Confidence: {conf.verified ?? 0} verified · {conf.known ?? 0} known ·{' '}
            {conf.estimated ?? 0} estimated · {conf.unverified ?? 0} unverified
          </div>
        )}

        {overrideBy && overriddenAt && (
          <div className="bg-grey-50 border border-grey-200 rounded-lg p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-grey-500 mb-1">
              Operator override recorded
            </p>
            <p className="text-sm text-grey-700">
              Overridden by <span className="font-semibold">{overrideBy}</span> on{' '}
              <span className="font-semibold">{formatDate(overriddenAt)}</span>
            </p>
            {overrideReason && (
              <p className="text-sm text-grey-700 mt-1 italic">"{overrideReason}"</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
